import React, { useEffect, useMemo, useRef, useState } from "react";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { useShallow } from "zustand/react/shallow";
import { SearchBar } from "@/components/common/SearchBar";
import { LoadingState } from "@/components/common/LoadingState";
import { SectionRule } from "components/common/SectionRule";
import { MetaAddButton } from "@/components/main/datasets/DatasetMetaSection";
import { ListEmptyShell } from "@/components/main/overview/ListEmptyShell";
import { VersionChip } from "@/components/main/utils/common/VersionChip";
import { TeamRow } from "@/components/main/utils/teams/TeamRow";
import { createTeam } from "@/components/main/utils/teams/TeamsApi";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import { useTeamsStore } from "@/components/main/stores/teamsStore";
import { useAppInitializationStore } from "@/components/main/stores/appInitializationStore";
import { useOnlineStatus } from "@/components/hooks/useOnlineStatus";
import { useAccountScheduledForDeletion } from "@/components/hooks/useAccountScheduledForDeletion";
import {
  ColumnBottomSpacer,
  ColumnTopSpacer,
  LIST_MAX_HEIGHT,
} from "@/components/main/pages/pageColumn";

/// Main-app Teams page. Mirrors the Overview / Datasets shell: a centered column
/// with a fixed filter bar and a scrolling list whose section header sticks.
/// Each team is an expandable `TeamRow` (members + description inline); clicking
/// the name/icon enters the team.
export const TeamsPage: React.FC = () => {
  const isOnline = useOnlineStatus();
  const isAccountScheduled = useAccountScheduledForDeletion();
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const [filter, setFilter] = useState("");
  // Page-owned so expanding one row collapses any other.
  const [openId, setOpenId] = useState<string | null>(null);

  const { teams, error, fetchTeamsList, addTeam } = useTeamsStore(
    useShallow((state) => ({
      teams: state.teams,
      error: state.error,
      fetchTeamsList: state.fetchTeamsList,
      addTeam: state.addTeam,
    })),
  );

  const { isAppInitialized, teamsInitialized } = useAppInitializationStore(
    useShallow((state) => ({
      isAppInitialized: state.isAppInitialized(),
      teamsInitialized: state.teamsInitialized,
    })),
  );

  const initializedRef = useRef(false);
  useEffect(() => {
    if (!initializedRef.current && navigator.onLine) {
      initializedRef.current = true;
      void fetchTeamsList({
        showLoading: !isAppInitialized && !teamsInitialized,
        refresh: teamsInitialized,
      });
    }
    // Polling is handled by the Sidebar's 5s interval — no duplicate here.
  }, [fetchTeamsList, teamsInitialized, isAppInitialized]);

  // Immediate refetch when connectivity is restored.
  const prevOnlineRef = useRef(isOnline);
  useEffect(() => {
    const wasOnline = prevOnlineRef.current;
    prevOnlineRef.current = isOnline;
    if (isOnline && !wasOnline) {
      void fetchTeamsList({ showLoading: false, refresh: true });
    }
  }, [isOnline, fetchTeamsList]);

  const setRowExpanded = (id: string, expanded: boolean) =>
    setOpenId((cur) => (expanded ? id : cur === id ? null : cur));

  const handleCreateTeamClick = async () => {
    if (isCreatingTeam) return;
    setIsCreatingTeam(true);
    try {
      const team = await createTeam();
      addTeam(team);
    } catch (err: unknown) {
      showNotificationGlobal(
        err instanceof Error ? err.message : "Failed to create team",
        "error",
      );
    } finally {
      setIsCreatingTeam(false);
    }
  };

  const q = filter.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      q
        ? teams.filter((t) =>
            t.team_details_js().team_name_js().toLowerCase().includes(q),
          )
        : teams,
    [teams, q],
  );
  if (isAppInitialized && !teamsInitialized) {
    if (!isOnline) {
      return (
        <div className="flex items-center justify-center w-full h-full">
          <div className="offline-empty-state" role="status" aria-live="polite">
            <div className="empty-dot" />
            <div className="empty-text">You&apos;re offline</div>
            <div className="empty-sub">
              Your teams will appear when the connection restores
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center w-full h-full">
        <LoadingState size="large" isLoading label="Loading teams..." />
      </div>
    );
  }
  if (!isAppInitialized) return null;

  const isEmpty = teams.length === 0;

  return (
    <div
      className="relative flex flex-col items-center w-full h-full overflow-y-auto px-6"
      style={{ opacity: isOnline ? 1 : 0.55, transition: "opacity 0.2s ease" }}
    >
      <ColumnTopSpacer />
      <div
        className="w-full flex flex-col"
        style={{
          maxWidth: "45rem",
          paddingTop: "2.5rem",
          paddingBottom: "2.5rem",
        }}
      >
        {isEmpty ? (
          <ListEmptyShell
            icon={<GroupsOutlinedIcon sx={{ fontSize: 14 }} />}
            label="Teams"
            hint="Create a team to collaborate with others on shared models."
            countLabel="0 teams"
            addLabel="New team"
            addDisabled={isCreatingTeam || isAccountScheduled}
            onAdd={() => void handleCreateTeamClick()}
            search={{
              value: filter,
              onChange: setFilter,
              placeholder: "Filter teams…",
            }}
          />
        ) : (
          <>
            <div className="shrink-0 mb-6">
              <SearchBar
                value={filter}
                onChange={setFilter}
                placeholder="Filter teams…"
                size="lg"
              />
            </div>

            <div
              className="overflow-y-auto"
              style={{ maxHeight: LIST_MAX_HEIGHT }}
            >
              {error && (
                <div className="text-error text-small mb-4">{error}</div>
              )}

              {filtered.length > 0 && (
                <div className="mb-8">
                  <div className="sticky top-0 z-10 bg-bg overflow-hidden">
                    <SectionRule
                      inset
                      icon={<GroupsOutlinedIcon sx={{ fontSize: 14 }} />}
                      label="Teams"
                    />
                  </div>
                  {filtered.map((t) => (
                    <TeamRow
                      key={t.team_id_str()}
                      team={t}
                      expanded={openId === t.team_id_str()}
                      onExpandedChange={(exp) =>
                        setRowExpanded(t.team_id_str(), exp)
                      }
                    />
                  ))}
                </div>
              )}

              {filtered.length === 0 && (
                <div className="text-center py-12 text-small text-fg-muted italic">
                  No teams match “{filter.trim()}”.
                </div>
              )}
            </div>

            {/* Pinned below the scroll: the count + New team pill stay visible
                while the list scrolls, and the pulse ring is never clipped. */}
            <div className="shrink-0 flex items-center mt-6">
              <span
                className="font-mono text-fg-muted"
                style={{
                  fontSize: "var(--font-size-x-small)",
                  letterSpacing: "0.04em",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {`${teams.length} ${teams.length === 1 ? "team" : "teams"}`}
              </span>
              {!isAccountScheduled && (
                <span className="ml-auto">
                  <MetaAddButton
                    label="New team"
                    disabled={isCreatingTeam}
                    onClick={() => void handleCreateTeamClick()}
                  />
                </span>
              )}
            </div>
          </>
        )}
      </div>

      <ColumnBottomSpacer />
      <div className="absolute bottom-4 right-4 z-10">
        <VersionChip />
      </div>
    </div>
  );
};
